import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, MapPin, Package, Truck, User2, AlertOctagon, XCircle, CheckCircle2, X } from "lucide-react";
import { api, errorToMessage } from "@/lib/apiClient";
import {
  useAuth, canInventoryLocation, canInventoryReserve,
  canInventoryPackShip, canInventoryScrapLost,
} from "@/lib/auth";
import { INV } from "@/constants/testIds";
import { formatRam, formatStorage } from "@/lib/specs";

const STATUS_STYLES = {
  AVAILABLE: "bg-emerald-100 text-emerald-800",
  RESERVED: "bg-sky-100 text-sky-800",
  PACKED: "bg-indigo-100 text-indigo-800",
  SHIPPED: "bg-violet-100 text-violet-800",
  SCRAP: "bg-rose-100 text-rose-800",
  LOST: "bg-amber-100 text-amber-800",
};

function Modal({ title, open, onClose, children }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-slate-900/40 p-4">
      <div className="w-full max-w-lg rounded-lg bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-slate-100 px-5 py-3">
          <h3 className="text-sm font-semibold text-slate-900">{title}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700">
            <X size={16} />
          </button>
        </div>
        <div className="px-5 py-4">{children}</div>
      </div>
    </div>
  );
}

function TextField({ testId, label, ...rest }) {
  return (
    <label className="block">
      <span className="block text-xs font-medium text-slate-500 mb-1">{label}</span>
      <input
        data-testid={testId}
        {...rest}
        className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-slate-800"
      />
    </label>
  );
}

export default function InventoryDetailPage() {
  const { passport_id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // ------- location form
  const [locForm, setLocForm] = useState({
    warehouse: "", rack: "", shelf: "", bin: "",
    location_remarks: "", location_barcode: "",
  });

  // ------- modals
  const [modal, setModal] = useState(null); // "reserve" | "pack" | "ship" | "scrap" | "lost"
  const [busy, setBusy] = useState(false);
  const [modalError, setModalError] = useState(null);

  const [resForm, setResForm] = useState({
    customer_name: "", customer_mobile: "", customer_email: "",
    address_line1: "", city: "", state: "", pincode: "",
    platform: "OFFLINE", selling_price: "", remarks: "",
  });
  const [packForm, setPackForm] = useState({
    verify_serial: "",
    checklist: {
      passport_verified: false, serial_verified: false,
      charger_included: false, accessories_included: false,
      outer_box_sealed: false, label_pasted: false,
    },
    remarks: "",
  });
  const [shipForm, setShipForm] = useState({
    courier_partner: "", awb_no: "",
    dispatch_date: new Date().toISOString().slice(0, 10),
    remarks: "",
  });
  const [scrapForm, setScrapForm] = useState({
    reason: "", remarks: "", approval_confirmed: false,
  });
  const [lostForm, setLostForm] = useState({
    reason: "", lost_date: new Date().toISOString().slice(0, 10),
    remarks: "", police_complaint_no: "",
  });

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get(`/inventory/${passport_id}`);
      setDetail(data);
      setLocForm({
        warehouse: data.warehouse || "",
        rack: data.rack || "",
        shelf: data.shelf || "",
        bin: data.bin || "",
        location_remarks: data.location_remarks || "",
        location_barcode: data.location_barcode || "",
      });
    } catch (e) {
      setError(errorToMessage(e, "Failed to load inventory record"));
    } finally {
      setLoading(false);
    }
  }, [passport_id]);

  useEffect(() => { load(); }, [load]);

  const saveLocation = async () => {
    setError(null);
    try {
      await api.post(`/inventory/${passport_id}/location`, locForm);
      await load();
    } catch (e) { setError(errorToMessage(e, "Failed to save location")); }
  };

  const runModal = async (op, body) => {
    setBusy(true); setModalError(null);
    try {
      await api.post(`/inventory/${passport_id}/${op}`, body);
      setModal(null);
      await load();
    } catch (e) { setModalError(errorToMessage(e, "Operation failed")); }
    finally { setBusy(false); }
  };

  if (loading) return <div className="text-sm text-slate-400">Loading…</div>;
  if (!detail) return <div className="text-sm text-rose-700">{error || "Not found"}</div>;

  const isTerminal = ["SCRAP", "LOST"].includes(detail.current_status);
  const canLocation = canInventoryLocation(user) && !isTerminal;
  const canReserve = canInventoryReserve(user) && detail.current_status === "AVAILABLE";
  const canPack = canInventoryPackShip(user) && detail.current_status === "RESERVED";
  const canShip = canInventoryPackShip(user) && detail.current_status === "PACKED";
  const canScrapLost = canInventoryScrapLost(user) && !isTerminal;

  return (
    <div className="space-y-6" data-testid="inventory-detail-page">
      <button onClick={() => navigate("/admin/inventory")} className="text-sm text-slate-600 inline-flex items-center gap-1">
        <ArrowLeft size={14} /> Back to inventory
      </button>

      {/* Header */}
      <div data-testid={INV.detailHeader} className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="text-xs uppercase tracking-widest text-slate-400">Digital Passport</div>
            <div className="mt-1 text-lg font-semibold text-slate-900">{detail.brand} {detail.model}</div>
            <div className="mt-0.5 text-sm text-slate-500">{detail.processor} · {formatRam(detail.ram_gb)} · {formatStorage(detail.storage_gb, detail.storage_type)}</div>
            <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-x-8 gap-y-2 text-xs">
              <div><span className="text-slate-400">Passport #</span><div className="font-mono text-slate-800">{detail.passport_number}</div></div>
              <div><span className="text-slate-400">Serial</span><div className="font-mono text-slate-800">{detail.serial_number}</div></div>
              <div data-testid={INV.detailQCEngineer}><span className="text-slate-400">QC by</span><div className="text-slate-800">{detail.qc_engineer || "—"}</div></div>
              <div><span className="text-slate-400">Purchase date</span><div className="text-slate-800">{detail.purchase_date || "—"}</div></div>
              <div><span className="text-slate-400">Available since</span><div className="text-slate-800">{detail.available_since ? new Date(detail.available_since).toLocaleDateString() : "—"}</div></div>
              <div data-testid={INV.detailLocation}><span className="text-slate-400"><MapPin size={10} className="inline" /> Location</span><div className="text-slate-800">{detail.current_location || "Unassigned"}</div></div>
              <div><span className="text-slate-400">Customer</span><div className="text-slate-800">{detail.reserved_by_customer || "—"}</div></div>
              <div><span className="text-slate-400">Order</span><div className="font-mono text-slate-800">{detail.order_internal_no || "—"}</div></div>
            </div>
          </div>
          <div data-testid={INV.detailStatus} className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-medium ${STATUS_STYLES[detail.current_status] || "bg-slate-100 text-slate-700"}`}>
            {detail.current_status}
          </div>
        </div>
      </div>

      {error && <div className="rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</div>}

      {/* Warehouse location card */}
      <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex items-center justify-between mb-3">
          <div className="text-xs uppercase tracking-widest text-slate-400">Warehouse Location</div>
          {!canLocation && <div className="text-xs text-slate-400">Read-only</div>}
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <TextField label="Warehouse" testId={INV.locWarehouse} value={locForm.warehouse}
                     disabled={!canLocation} onChange={(e) => setLocForm({...locForm, warehouse: e.target.value})} />
          <TextField label="Rack" testId={INV.locRack} value={locForm.rack}
                     disabled={!canLocation} onChange={(e) => setLocForm({...locForm, rack: e.target.value})} />
          <TextField label="Shelf" testId={INV.locShelf} value={locForm.shelf}
                     disabled={!canLocation} onChange={(e) => setLocForm({...locForm, shelf: e.target.value})} />
          <TextField label="Bin" testId={INV.locBin} value={locForm.bin}
                     disabled={!canLocation} onChange={(e) => setLocForm({...locForm, bin: e.target.value})} />
          <TextField label="Location barcode (optional)" testId={INV.locBarcode} value={locForm.location_barcode}
                     disabled={!canLocation} onChange={(e) => setLocForm({...locForm, location_barcode: e.target.value})} />
          <TextField label="Remarks" testId={INV.locRemarks} value={locForm.location_remarks}
                     disabled={!canLocation} onChange={(e) => setLocForm({...locForm, location_remarks: e.target.value})} />
        </div>
        {canLocation && (
          <div className="mt-4 flex justify-end">
            <button data-testid={INV.locSaveBtn} onClick={saveLocation}
                    disabled={!locForm.warehouse || !locForm.rack || !locForm.shelf || !locForm.bin}
                    className="inline-flex items-center gap-2 rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-40">
              <MapPin size={14} /> Save Location
            </button>
          </div>
        )}
      </div>

      {/* Scrap/Lost detail cards */}
      {detail.scrap && (
        <div className="rounded-lg border border-rose-200 bg-rose-50 p-5 text-sm text-rose-900">
          <div className="font-semibold flex items-center gap-2"><AlertOctagon size={14} /> Scrapped</div>
          <div className="mt-1 text-xs"><span className="text-rose-700">Reason:</span> {detail.scrap.reason}</div>
          {detail.scrap.remarks && <div className="mt-0.5 text-xs">{detail.scrap.remarks}</div>}
        </div>
      )}
      {detail.lost && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-5 text-sm text-amber-900">
          <div className="font-semibold flex items-center gap-2"><XCircle size={14} /> Lost</div>
          <div className="mt-1 text-xs"><span className="text-amber-700">Date:</span> {detail.lost.lost_date} · <span className="text-amber-700">Reason:</span> {detail.lost.reason}</div>
          {detail.lost.police_complaint_no && <div className="mt-0.5 text-xs">FIR #: {detail.lost.police_complaint_no}</div>}
        </div>
      )}

      {/* Action bar */}
      <div className="sticky bottom-4 rounded-lg border border-slate-200 bg-white p-4 shadow-lg flex items-center justify-end gap-3 flex-wrap">
        {canReserve && (
          <button data-testid={INV.reserveOpenBtn} onClick={() => setModal("reserve")}
                  className="rounded-md bg-sky-600 px-4 py-2 text-sm font-medium text-white hover:bg-sky-500">
            <User2 size={14} className="inline mr-1" /> Reserve
          </button>
        )}
        {canPack && (
          <button data-testid={INV.packOpenBtn} onClick={() => setModal("pack")}
                  className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-500">
            <Package size={14} className="inline mr-1" /> Pack
          </button>
        )}
        {canShip && (
          <button data-testid={INV.shipOpenBtn} onClick={() => setModal("ship")}
                  className="rounded-md bg-violet-600 px-4 py-2 text-sm font-medium text-white hover:bg-violet-500">
            <Truck size={14} className="inline mr-1" /> Ship
          </button>
        )}
        {canScrapLost && (
          <>
            <button data-testid={INV.scrapOpenBtn} onClick={() => setModal("scrap")}
                    className="rounded-md bg-rose-600 px-4 py-2 text-sm font-medium text-white hover:bg-rose-500">
              <AlertOctagon size={14} className="inline mr-1" /> Scrap
            </button>
            <button data-testid={INV.lostOpenBtn} onClick={() => setModal("lost")}
                    className="rounded-md bg-amber-600 px-4 py-2 text-sm font-medium text-white hover:bg-amber-500">
              <XCircle size={14} className="inline mr-1" /> Lost
            </button>
          </>
        )}
      </div>

      {/* Reserve modal */}
      <Modal title="Reserve laptop" open={modal === "reserve"} onClose={() => setModal(null)}>
        {modalError && <div className="mb-3 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">{modalError}</div>}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <TextField label="Customer name*" testId={INV.reserveCustomerName} value={resForm.customer_name}
                     onChange={(e) => setResForm({...resForm, customer_name: e.target.value})} />
          <TextField label="Mobile*" testId={INV.reserveCustomerMobile} value={resForm.customer_mobile}
                     onChange={(e) => setResForm({...resForm, customer_mobile: e.target.value})} />
          <TextField label="Email" value={resForm.customer_email}
                     onChange={(e) => setResForm({...resForm, customer_email: e.target.value})} />
          <TextField label="City" testId={INV.reserveCity} value={resForm.city}
                     onChange={(e) => setResForm({...resForm, city: e.target.value})} />
          <label className="block">
            <span className="block text-xs font-medium text-slate-500 mb-1">Platform</span>
            <select data-testid={INV.reservePlatform} value={resForm.platform}
                    onChange={(e) => setResForm({...resForm, platform: e.target.value})}
                    className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm">
              {["OFFLINE","WALK_IN","AMAZON","SHOPIFY"].map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </label>
          <TextField label="Selling price (₹)" testId={INV.reservePrice} value={resForm.selling_price}
                     onChange={(e) => setResForm({...resForm, selling_price: e.target.value})} />
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <button onClick={() => setModal(null)} className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm">Cancel</button>
          <button data-testid={INV.reserveSubmit} disabled={busy || !resForm.customer_name || !resForm.customer_mobile}
                  onClick={() => runModal("reserve", resForm)}
                  className="rounded-md bg-sky-600 px-4 py-2 text-sm font-medium text-white hover:bg-sky-500 disabled:opacity-40">Reserve</button>
        </div>
      </Modal>

      {/* Pack modal */}
      <Modal title="Pack laptop" open={modal === "pack"} onClose={() => setModal(null)}>
        {modalError && <div className="mb-3 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">{modalError}</div>}
        <div className="mb-3 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
          Serial to verify: <span className="font-mono text-slate-900">{detail.serial_number}</span>
        </div>
        <TextField label="Retype serial to confirm" testId={INV.packSerialVerify} value={packForm.verify_serial}
                   onChange={(e) => setPackForm({...packForm, verify_serial: e.target.value})} />
        <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
          {[
            ["passport_verified", INV.packChkPassport, "Passport verified"],
            ["serial_verified", INV.packChkSerial, "Serial verified"],
            ["charger_included", INV.packChkCharger, "Charger included"],
            ["accessories_included", INV.packChkAccessories, "Accessories included"],
            ["outer_box_sealed", INV.packChkBox, "Outer box sealed"],
            ["label_pasted", INV.packChkLabel, "Label pasted"],
          ].map(([key, tid, label]) => (
            <label key={key} className="inline-flex items-center gap-2">
              <input type="checkbox" data-testid={tid} checked={packForm.checklist[key]}
                     onChange={(e) => setPackForm({...packForm, checklist: {...packForm.checklist, [key]: e.target.checked}})}
                     className="h-4 w-4 accent-indigo-600" />
              {label}
            </label>
          ))}
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <button onClick={() => setModal(null)} className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm">Cancel</button>
          <button data-testid={INV.packSubmit} disabled={busy || packForm.verify_serial !== detail.serial_number}
                  onClick={() => runModal("pack", packForm)}
                  className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-500 disabled:opacity-40">Pack</button>
        </div>
      </Modal>

      {/* Ship modal */}
      <Modal title="Ship laptop" open={modal === "ship"} onClose={() => setModal(null)}>
        {modalError && <div className="mb-3 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">{modalError}</div>}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <TextField label="Courier partner*" testId={INV.shipCourier} value={shipForm.courier_partner}
                     onChange={(e) => setShipForm({...shipForm, courier_partner: e.target.value})} />
          <TextField label="AWB / Tracking No*" testId={INV.shipAWB} value={shipForm.awb_no}
                     onChange={(e) => setShipForm({...shipForm, awb_no: e.target.value})} />
          <TextField label="Dispatch date*" testId={INV.shipDate} type="date" value={shipForm.dispatch_date}
                     onChange={(e) => setShipForm({...shipForm, dispatch_date: e.target.value})} />
          <TextField label="Remarks" value={shipForm.remarks}
                     onChange={(e) => setShipForm({...shipForm, remarks: e.target.value})} />
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <button onClick={() => setModal(null)} className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm">Cancel</button>
          <button data-testid={INV.shipSubmit} disabled={busy || !shipForm.courier_partner || !shipForm.awb_no || !shipForm.dispatch_date}
                  onClick={() => runModal("ship", shipForm)}
                  className="rounded-md bg-violet-600 px-4 py-2 text-sm font-medium text-white hover:bg-violet-500 disabled:opacity-40">
            <Truck size={14} className="inline mr-1" /> Ship
          </button>
        </div>
      </Modal>

      {/* Scrap modal */}
      <Modal title="Scrap laptop (SUPER_ADMIN)" open={modal === "scrap"} onClose={() => setModal(null)}>
        {modalError && <div className="mb-3 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">{modalError}</div>}
        <TextField label="Reason*" testId={INV.scrapReason} value={scrapForm.reason}
                   onChange={(e) => setScrapForm({...scrapForm, reason: e.target.value})} />
        <div className="mt-3">
          <label className="block text-xs font-medium text-slate-500 mb-1">Remarks</label>
          <textarea rows={3} data-testid={INV.scrapRemarks} value={scrapForm.remarks}
                    onChange={(e) => setScrapForm({...scrapForm, remarks: e.target.value})}
                    className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm" />
        </div>
        <label className="mt-3 inline-flex items-center gap-2 text-sm">
          <input type="checkbox" data-testid={INV.scrapApproval} checked={scrapForm.approval_confirmed}
                 onChange={(e) => setScrapForm({...scrapForm, approval_confirmed: e.target.checked})}
                 className="h-4 w-4 accent-rose-600" />
          I confirm this is authorised for scrap.
        </label>
        <div className="mt-4 flex justify-end gap-2">
          <button onClick={() => setModal(null)} className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm">Cancel</button>
          <button data-testid={INV.scrapSubmit} disabled={busy || !scrapForm.reason || !scrapForm.approval_confirmed}
                  onClick={() => runModal("scrap", scrapForm)}
                  className="rounded-md bg-rose-600 px-4 py-2 text-sm font-medium text-white hover:bg-rose-500 disabled:opacity-40">
            <AlertOctagon size={14} className="inline mr-1" /> Scrap
          </button>
        </div>
      </Modal>

      {/* Lost modal */}
      <Modal title="Mark as Lost (SUPER_ADMIN)" open={modal === "lost"} onClose={() => setModal(null)}>
        {modalError && <div className="mb-3 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">{modalError}</div>}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <TextField label="Reason*" testId={INV.lostReason} value={lostForm.reason}
                     onChange={(e) => setLostForm({...lostForm, reason: e.target.value})} />
          <TextField label="Lost date*" testId={INV.lostDate} type="date" value={lostForm.lost_date}
                     onChange={(e) => setLostForm({...lostForm, lost_date: e.target.value})} />
          <TextField label="Police FIR (optional)" testId={INV.lostPolice} value={lostForm.police_complaint_no}
                     onChange={(e) => setLostForm({...lostForm, police_complaint_no: e.target.value})} />
        </div>
        <div className="mt-3">
          <label className="block text-xs font-medium text-slate-500 mb-1">Remarks</label>
          <textarea rows={3} value={lostForm.remarks}
                    onChange={(e) => setLostForm({...lostForm, remarks: e.target.value})}
                    className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm" />
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <button onClick={() => setModal(null)} className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm">Cancel</button>
          <button data-testid={INV.lostSubmit} disabled={busy || !lostForm.reason || !lostForm.lost_date}
                  onClick={() => runModal("lost", lostForm)}
                  className="rounded-md bg-amber-600 px-4 py-2 text-sm font-medium text-white hover:bg-amber-500 disabled:opacity-40">
            <XCircle size={14} className="inline mr-1" /> Mark Lost
          </button>
        </div>
      </Modal>
    </div>
  );
}
